import { DateTime } from 'luxon'
import * as Types from '@aeppic/types'
import type { ParsedField } from '@aeppic/forms-parser'
import { CURRENCIES, CURRENCY_MAP } from '@aeppic/shared/currencies'
import { formatCurrency as formatCurrencyFromBaseUnits, type FormatCurrencyOptions } from '@aeppic/shared/common-formatting'
import type { TextFormatter, TextFormatterContext } from '../model/field-formatter.js'

export type FieldInfo = ParsedField

export type FieldFormattingAPI = {
  toString: {
    (): string
    (value: any, fieldInfo: FieldInfo, options?: FormatOptions): string
  }
  currency: (value: CurrencyValue | number, options?: CurrencyFormatOptions) => string
  address: (value: AddressValue, options?: AddressFormatOptions) => string
  geolocation: (value: GeoValue, options?: GeoFormatOptions) => string
  duration: (value: DurationValue) => string
  file: (value: FileValue) => string
  date: (value: string, subType: string, options?: FormatOptions) => string
  ref: (value: RefValue) => string
  CURRENCIES: typeof CURRENCIES
  CURRENCY_MAP: typeof CURRENCY_MAP
}

export type FormattingAPI = FieldFormattingAPI & {
  formatText(valueOrFormattingExpression: any, formattingExpression?: string, context?: TextFormatterContext): TextFormatter | string
  formatPastDateTime(datetime: string, options?: { locale?: string }): string
  formatCurrency(value: Types.CurrencyField, options?: FormatCurrencyOptions): string
}

export interface FormatOptions {
  locale?: string
}

export interface AddressFormatOptions extends FormatOptions {
  /** Same as `style: 'short'` */
  short?: boolean
  /**
   * - `line` (default): everything on one line, comma separated
   * - `short`: street and postal code/city only
   * - `block`: one part per line (`\n`)
   * - `postal`: address label after DIN 5008; the country only for foreign
   *   addresses, as the last line in capitals
   */
  style?: 'line' | 'short' | 'block' | 'postal'
  /** Country that `postal` leaves out (ISO 3166-1 alpha-2, default `DE`) */
  homeCountry?: string
}

export interface GeoFormatOptions extends FormatOptions {
  format?: 'decimal' | 'dms'
}

export interface CurrencyValue {
  amount: number
  currency: string
  precision: number
}

export interface AddressValue {
  street?: string
  streetNumber?: string
  postalCode?: string
  city?: string
  state?: string
  country?: string
  supplement?: string
  [key: string]: any
}

export interface GeoValue {
  lat: number | string
  lon: number | string
  accuracy?: number | null
  source?: string | null
  capturedAt?: number | null
}

export interface DurationValue {
  years?: number
  months?: number
  weeks?: number
  days?: number
  hours?: number
  minutes?: number
  seconds?: number
}

export interface FileValue {
  name?: string
  type?: string
  size?: number
}

export interface RefValue {
  id?: string
  text?: string
}

export { CURRENCIES, CURRENCY_MAP }

function resolveLocale(locale?: string): string | undefined {
  if (!locale || locale === 'auto') {
    return undefined
  }
  return locale
}

export interface CurrencyFormatOptions extends FormatOptions {
  defaultCurrency?: string
  defaultPrecision?: number
}

export function formatCurrency(value: CurrencyValue | number | null | undefined, options: CurrencyFormatOptions = {}): string {
  if (value == null) {
    return ''
  }

  if (typeof value === 'number') {
    const currency = options.defaultCurrency
    if (!currency) {
      return String(value)
    }

    const precision = options.defaultPrecision ?? (CURRENCY_MAP[currency]?.precision ?? 2)
    const locale = resolveLocale(options.locale)

    return formatCurrencyFromBaseUnits({ amount: value, currency, precision }, { locale })
  }

  if (value.currency == null) {
    return ''
  }

  const { amount, currency, precision } = value
  const locale = resolveLocale(options.locale)

  return formatCurrencyFromBaseUnits({ amount, currency, precision }, { locale })
}

export function formatAddress(value: AddressValue, options: AddressFormatOptions = {}): string {
  if (value == null) {
    return ''
  }

  const { street, streetNumber, postalCode, city, state, supplement } = value
  const style = options.style ?? (options.short ? 'short' : 'line')

  const streetPart = [street, streetNumber].filter(Boolean).join(' ')
  const cityPart = [postalCode, city].filter(Boolean).join(' ')

  if (style === 'short') {
    return [streetPart, cityPart].filter(Boolean).join(', ')
  }

  const country = countryName(value.country, options.locale)

  if (style === 'postal') {
    const homeCountry = (options.homeCountry ?? 'DE').toUpperCase()
    const foreign = value.country && countryCode(value.country) !== homeCountry
    return [supplement, streetPart, cityPart, foreign ? country.toLocaleUpperCase(options.locale) : ''].filter(Boolean).join('\n')
  }

  const parts = [supplement, streetPart, cityPart, state, country].filter(Boolean)
  return parts.join(style === 'block' ? '\n' : ', ')
}

/** Display name for an ISO 3166-1 alpha-2 code (`DE` → `Deutschland`); anything else is returned as stored */
function countryName(country: string | undefined, locale?: string): string {
  if (!country) {
    return ''
  }

  if (!/^[A-Za-z]{2}$/.test(country.trim())) {
    return country
  }

  try {
    return new Intl.DisplayNames(resolveLocale(locale), { type: 'region' }).of(country.trim().toUpperCase()) ?? country
  } catch {
    return country
  }
}

/** ISO code for a stored country: a code as is, a German or English country name looked up */
function countryCode(country: string): string | null {
  const trimmed = country.trim()

  if (/^[A-Za-z]{2}$/.test(trimmed)) {
    return trimmed.toUpperCase()
  }

  const alias = COUNTRY_ALIASES[trimmed.toUpperCase()]

  if (alias) {
    return alias
  }

  const wanted = trimmed.toLowerCase()

  for (const locale of ['de', 'en']) {
    try {
      const names = new Intl.DisplayNames(locale, { type: 'region' })

      for (const code of KNOWN_COUNTRY_CODES) {
        if (names.of(code)?.toLowerCase() === wanted) {
          return code
        }
      }
    } catch {
      return null
    }
  }

  return null
}

// Older spellings found in stored addresses
const COUNTRY_ALIASES: Record<string, string> = { D: 'DE', DEU: 'DE', A: 'AT', AUT: 'AT', CHE: 'CH' }

// Countries whose stored names are recognised (codes always are)
const KNOWN_COUNTRY_CODES = ['DE', 'AT', 'CH', 'LI', 'LU', 'BE', 'NL', 'FR', 'IT', 'PL', 'CZ', 'DK', 'ES', 'PT', 'GB', 'US', 'SE', 'NO', 'FI', 'HU', 'TR', 'UA', 'RU']

export function formatGeolocation(value: GeoValue, options: GeoFormatOptions = {}): string {
  if (value == null) {
    return ''
  }

  const lat = Number(value.lat)
  const lon = Number(value.lon)

  if (isNaN(lat) || isNaN(lon)) {
    return ''
  }

  if (options.format === 'dms') {
    return `${toDMS(lat, 'N', 'S')} ${toDMS(lon, 'E', 'W')}`
  }

  return `${lat}, ${lon}`
}

function toDMS(decimal: number, positive: string, negative: string): string {
  const direction = decimal >= 0 ? positive : negative
  const abs = Math.abs(decimal)
  const degrees = Math.floor(abs)
  const minutesDecimal = (abs - degrees) * 60
  const minutes = Math.floor(minutesDecimal)
  const seconds = ((minutesDecimal - minutes) * 60).toFixed(1)
  return `${degrees}°${minutes}'${seconds}"${direction}`
}

export function formatDuration(value: DurationValue): string {
  if (value == null) {
    return ''
  }

  const parts: string[] = []

  if (value.years)   parts.push(`${value.years}y`)
  if (value.months)  parts.push(`${value.months}mo`)
  if (value.weeks)   parts.push(`${value.weeks}w`)
  if (value.days)    parts.push(`${value.days}d`)
  if (value.hours)   parts.push(`${value.hours}h`)
  if (value.minutes) parts.push(`${value.minutes}m`)
  if (value.seconds) parts.push(`${value.seconds}s`)

  return parts.join(' ') || '0m'
}

export function formatFile(value: FileValue): string {
  if (value == null) {
    return ''
  }

  return value.name || ''
}

export function formatDate(value: string, subType: string, options: FormatOptions = {}): string {
  if (value == null || value === '') {
    return ''
  }

  const locale = resolveLocale(options.locale)

  switch (subType) {
    case 'date': {
      const d = new Date(value + 'T00:00:00')
      if (isNaN(d.getTime())) return value
      return d.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' })
    }
    case 'datetime': {
      const d = new Date(value)
      if (isNaN(d.getTime())) return value
      return d.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' }) +
        ', ' + d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })
    }
    case 'time': {
      const parts = value.split(':')
      return parts.slice(0, 2).join(':')
    }
    case 'monthofyear': {
      const dt = DateTime.fromFormat(value, 'yyyy-MM')
      if (!dt.isValid) return value
      return dt.toFormat('MMMM yyyy')
    }
    default:
      return value
  }
}

export function formatRef(value: RefValue): string {
  if (value == null) {
    return ''
  }

  return value.text || value.id || ''
}

export function toString(value: any, fieldInfo: FieldInfo, options: FormatOptions = {}): string {
  if (value == null) {
    return ''
  }

  const { type } = fieldInfo
  const subType = (fieldInfo as any).subType

  switch (type) {
    case 'string':
      if (subType === 'date' || subType === 'datetime' || subType === 'time' || subType === 'monthofyear') {
        return formatDate(value, subType, options)
      }
      return String(value)

    case 'number':
      if (subType === 'month') {
        const dt = DateTime.fromObject({ month: value })
        if (!dt.isValid) return String(value)
        return dt.toFormat('MMMM')
      }
      return String(value)

    case 'boolean': {
      const locale = resolveLocale(options.locale)
      const isGerman = locale ? locale.startsWith('de') : true
      return value ? (isGerman ? 'Ja' : 'Yes') : (isGerman ? 'Nein' : 'No')
    }

    case 'object':
      return formatObjectValue(value, subType, options)

    default:
      return String(value)
  }
}

function formatObjectValue(value: any, subType: string, options: FormatOptions): string {
  switch (subType) {
    case 'currency':
      return formatCurrency(value, options)
    case 'address':
      return formatAddress(value, { short: true, ...options })
    case 'geolocation':
      return formatGeolocation(value, options)
    case 'duration':
      return formatDuration(value)
    case 'file':
    case 'image':
      return formatFile(value)
    case 'ref':
      return formatRef(value)
    default:
      return '—'
  }
}

export function createFormattingAPI(getLocale: () => string | undefined) {
  function withLocale<T extends FormatOptions>(options: T): T {
    if (options.locale == null) {
      return { ...options, locale: getLocale() }
    }
    return options
  }

  function formatValueToString(): string
  function formatValueToString(value: any, fieldInfo: FieldInfo, options?: FormatOptions): string
  function formatValueToString(value?: any, fieldInfo?: FieldInfo, options: FormatOptions = {}) {
    if (arguments.length === 0) {
      return '[object AeppicFormatting]'
    }

    return toString(value, fieldInfo, withLocale(options))
  }

  return {
    toString: formatValueToString,

    currency: (value: CurrencyValue | number, options: CurrencyFormatOptions = {}) =>
      formatCurrency(value, withLocale(options)),

    address: (value: AddressValue, options: AddressFormatOptions = {}) =>
      formatAddress(value, withLocale(options)),

    geolocation: (value: GeoValue, options: GeoFormatOptions = {}) =>
      formatGeolocation(value, options),

    duration: (value: DurationValue) =>
      formatDuration(value),

    file: (value: FileValue) =>
      formatFile(value),

    date: (value: string, subType: string, options: FormatOptions = {}) =>
      formatDate(value, subType, withLocale(options)),

    ref: (value: RefValue) =>
      formatRef(value),

    CURRENCIES,
    CURRENCY_MAP,
  }
}
